<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { onMounted, reactive, ref } from 'vue'

import {
  KIND_LABELS,
  LEVEL_LABELS,
  LEVEL_TAG,
  STATUS_LABELS,
  STATUS_TAG,
  canAct,
  closeException,
  handlingException,
  listExceptions,
  type ExceptionAction,
  type ExceptionKind,
  type ExceptionLevel,
  type ExceptionRow,
  type ExceptionStatus,
} from '../api/exceptions'

const rows = ref<ExceptionRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
/** 受理 / 关闭失败的错误：409 的原因与 trace_id 都要露出来 */
const refError = ref<unknown>(null)
const notice = ref('')

const query = reactive({
  keyword: '',
  level: '' as ExceptionLevel | '',
  status: '' as ExceptionStatus | '',
  kind: '' as ExceptionKind | '',
  page: 1,
  size: 20,
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listExceptions({
      page: query.page,
      size: query.size,
      keyword: query.keyword.trim() || undefined,
      level: query.level || undefined,
      status: query.status || undefined,
      kind: query.kind || undefined,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

async function act(kind: ExceptionAction, row: ExceptionRow): Promise<void> {
  notice.value = ''
  refError.value = null
  try {
    if (kind === 'handling') await handlingException(row.id)
    else await closeException(row.id)
    await load(
      kind === 'handling'
        ? `异常 #${row.id} 已受理`
        : `异常 #${row.id} 已关闭，运单状态已由后端回置`,
    )
  } catch (e) {
    refError.value = e
  }
}
</script>

<template>
  <div class="yc-exception">
    <header class="yc-exception__bar">
      <el-input
        v-model="query.keyword"
        class="yc-exception__keyword"
        placeholder="异常描述 / 运单号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select
        v-model="query.level"
        class="yc-exception__ref"
        placeholder="全部级别"
        clearable
        @change="search"
      >
        <el-option
          v-for="(label, value) in LEVEL_LABELS"
          :key="value"
          :label="label"
          :value="value"
        />
      </el-select>
      <el-select
        v-model="query.status"
        class="yc-exception__ref"
        placeholder="全部状态"
        clearable
        @change="search"
      >
        <el-option
          v-for="(label, value) in STATUS_LABELS"
          :key="value"
          :label="label"
          :value="value"
        />
      </el-select>
      <el-select
        v-model="query.kind"
        class="yc-exception__ref"
        placeholder="全部类型"
        clearable
        @change="search"
      >
        <el-option
          v-for="(label, value) in KIND_LABELS"
          :key="value"
          :label="label"
          :value="value"
        />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-exception__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-exception__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-exception__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column label="所属运单" min-width="150" fixed="left" show-overflow-tooltip>
          <template #default="{ row }">{{ row.shipment_doc_no || '—' }}</template>
        </el-table-column>
        <el-table-column label="级别" width="90">
          <template #default="{ row }">
            <el-tag :type="LEVEL_TAG[row.level as ExceptionLevel] ?? 'info'" size="small">
              {{ LEVEL_LABELS[row.level as ExceptionLevel] ?? row.level }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="类型" width="90">
          <template #default="{ row }">{{ KIND_LABELS[row.kind as ExceptionKind] ?? row.kind }}</template>
        </el-table-column>
        <el-table-column prop="desc" label="描述" min-width="240" show-overflow-tooltip />
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as ExceptionStatus] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as ExceptionStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="handled_by" label="处理人" min-width="100" show-overflow-tooltip>
          <template #default="{ row }">{{ row.handled_by || '—' }}</template>
        </el-table-column>
        <el-table-column label="处理时间" min-width="150">
          <template #default="{ row }">{{ formatDateTime(row.handled_at) }}</template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">{{ row.remark || '—' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="170" fixed="right">
          <template #default="{ row }">
            <el-popconfirm
              v-if="canAct(row.status as ExceptionStatus, 'handling')"
              title="确认受理该异常？状态将变为处理中。"
              width="300"
              @confirm="act('handling', row)"
            >
              <template #reference>
                <el-button v-perm="'pod:exception:handle'" link type="primary">处理中</el-button>
              </template>
            </el-popconfirm>
            <el-popconfirm
              v-if="canAct(row.status as ExceptionStatus, 'close')"
              title="确认关闭该异常？运单状态会由后端回置为已签收或已到达。"
              width="380"
              @confirm="act('close', row)"
            >
              <template #reference>
                <el-button v-perm="'pod:exception:close'" link type="danger">关闭</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="没有签收异常"
        description="拒收、破损签收会自动生成异常，处理完记得关闭，运单才能继续流转。"
      />
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-exception__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />
  </div>
</template>

<style scoped lang="scss">
.yc-exception {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 220px;
  }

  &__ref {
    width: 140px;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }
}
</style>
